/**
 * Platform-neutral icon data (D-73): prototype sprites stay stroked; MD3 gaps use Material Symbols
 * Rounded path data (filled). Directional glyphs set `ICON_MIRROR` for RTL. No DOM or React:
 * buyer-mobile imports from `@saay/ui/icon-paths`.
 */

/** A circle as two half arcs: identical to `<circle cx cy r>` when stroked. */
function circle(cx: number, cy: number, r: number): string {
  return `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`;
}

/** A rectangle, with rounded corners when `rx` is given: identical to `<rect … rx>`. */
function rect(x: number, y: number, width: number, height: number, rx = 0): string {
  if (rx === 0) {
    return `M${x} ${y}h${width}v${height}h${-width}Z`;
  }
  const w = width - 2 * rx;
  const h = height - 2 * rx;
  const corner = (dx: number, dy: number) => `a${rx} ${rx} 0 0 1 ${dx} ${dy}`;
  return `M${x + rx} ${y}h${w}${corner(rx, rx)}v${h}${corner(-rx, rx)}h${-w}${corner(-rx, -rx)}v${-h}${corner(rx, -rx)}Z`;
}

/** The panel prototypes' sprites (admin-web `a-*`, merchant-web `i-*`), the default set. */
export const ICON_PATHS = {
  alert: ['M12 3 2 21h20L12 3Z', 'M12 9v5M12 18h.01'],
  arrow: ['m9 18 6-6-6-6'],
  'arrow-left': ['M19 12H5M11 18l-6-6 6-6'],
  /** D-85 stat tile and card link (the references' ↗); mirrored in RTL to point inline-end. */
  'arrow-outward': ['M7 17 17 7', 'M8 7h9v9'],
  bag: ['M5 8h14l-1 13H6L5 8Z', 'M9 9V6a3 3 0 0 1 6 0v3'],
  bank: ['M3 9h18M5 9v9M9 9v9M15 9v9M19 9v9M3 19h18M12 3l9 5H3l9-5Z'],
  bell: ['M18 9a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9ZM10 22h4'],
  calendar: [rect(3, 5, 18, 16, 2), 'M7 3v4M17 3v4M3 10h18'],
  card: [rect(3, 5, 18, 14, 3), 'M3 10h18M7 15h4'],
  check: ['m5 12 4 4L19 6'],
  'chevron-left': ['m15 18-6-6 6-6'],
  clock: [circle(12, 12, 9), 'M12 7v5l3 2'],
  close: ['m6 6 12 12M18 6 6 18'],
  doc: ['M6 3h9l3 3v15H6V3Z', 'M14 3v4h4M9 12h6M9 16h6'],
  download: ['M12 3v12m0 0 4-4m-4 4-4-4M4 19h16'],
  edit: ['m4 20 4.5-1 10-10L15 5.5l-10 10L4 20Z', 'm13.5 7 3.5 3.5'],
  eye: ['M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z', circle(12, 12, 2.5)],
  gift: [
    'M4 10h16v11H4zM3 6h18v4H3zM12 6v15',
    'M12 6H8.5a2.5 2.5 0 1 1 2.5-2.5L12 6Zm0 0h3.5A2.5 2.5 0 1 0 13 3.5L12 6Z',
  ],
  home: ['m3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V10Z'],
  info: [circle(12, 12, 9), 'M12 11v6M12 7h.01'],
  /** A key (passkeys and device security keys). */
  key: [circle(8, 15, 4), 'm10.8 12.2 9.2-9.2M17 6l3 3M14.5 8.5l2 2'],
  // The app PIN (a field of masked digits), distinct from the account password's lock (W4).
  pin: [rect(2, 7, 20, 10, 3), 'M7 12h.01M12 12h.01M17 12h.01'],
  moon: ['M20 15.5A8.5 8.5 0 0 1 8.5 4 8.5 8.5 0 1 0 20 15.5Z'],
  more: [circle(5, 12, 1), circle(12, 12, 1), circle(19, 12, 1)],
  plus: ['M12 5v14M5 12h14'],
  qr: [
    rect(3, 3, 7, 7, 1),
    rect(14, 3, 7, 7, 1),
    rect(3, 14, 7, 7, 1),
    'M14 14h3v3h-3zM18 18h3v3h-3zM18 14h3M14 18v3',
  ],
  receipt: ['M6 3h12v18l-3-2-3 2-3-2-3 2V3Z', 'M9 8h6M9 12h6M9 16h3'],
  refresh: ['M20 7v5h-5M4 17v-5h5', 'M6.1 9a7 7 0 0 1 11.2-2L20 12M4 12l2.7 5a7 7 0 0 0 11.2-2'],
  return: ['M9 7 4 12l5 5', 'M4 12h10a6 6 0 0 1 6 6'],
  search: [circle(11, 11, 7), 'm20 20-4-4'],
  settings: [
    circle(12, 12, 3),
    'M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z',
  ],
  share: [circle(6, 12, 3), circle(18, 6, 3), circle(18, 18, 3), 'm8.6 10.5 6.8-3M8.6 13.5l6.8 3'],
  shield: ['M12 3 4 6v5c0 5 3.4 8.4 8 10 4.6-1.6 8-5 8-10V6l-8-3Z', 'm9 12 2 2 4-5'],
  store: ['M4 10v11h16V10M3 4h18l-2 6H5L3 4Z', 'M9 21v-6h6v6'],
  sun: [
    circle(12, 12, 4),
    'M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  ],
  user: [circle(12, 8, 4), 'M4 21a8 8 0 0 1 16 0'],
  users: [
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  ],
  wallet: [
    'M4 6h15a2 2 0 0 1 2 2v10H5a2 2 0 0 1-2-2V7a3 3 0 0 1 3-3h11',
    'M16 11h5v4h-5a2 2 0 0 1 0-4Z',
  ],
  'arrow-back': [
    'm7.825 13l4.9 4.9q.3.3.288.7t-.313.7q-.3.275-.7.288t-.7-.288l-6.6-6.6q-.15-.15-.213-.325T4.426 12t.063-.375t.212-.325l6.6-6.6q.275-.275.688-.275t.712.275q.3.3.3.713t-.3.712L7.825 11H19q.425 0 .713.288T20 12t-.288.713T19 13z',
  ],
  'calendar-today': [
    'M5 22q-.825 0-1.412-.587T3 20V6q0-.825.588-1.412T5 4h1V3q0-.425.288-.712T7 2t.713.288T8 3v1h8V3q0-.425.288-.712T17 2t.713.288T18 3v1h1q.825 0 1.413.588T21 6v14q0 .825-.587 1.413T19 22zm0-2h14V10H5z',
  ],
  'check-circle': [
    'm10.6 13.8l-2.15-2.15q-.275-.275-.7-.275t-.7.275t-.275.7t.275.7L9.9 15.9q.3.3.7.3t.7-.3l5.65-5.65q.275-.275.275-.7t-.275-.7t-.7-.275t-.7.275zM12 22q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22',
  ],
  'chevron-down': [
    'M12 14.95q-.2 0-.375-.062t-.325-.213l-4.6-4.6q-.275-.275-.275-.7t.275-.7t.7-.275t.7.275l3.9 3.9l3.9-3.9q.275-.275.7-.275t.7.275t.275.7t-.275.7l-4.6 4.6q-.15.15-.325.213T12 14.95',
  ],
  'chevron-right': [
    'M12.6 12L8.7 8.1q-.275-.275-.275-.7t.275-.7t.7-.275t.7.275l4.6 4.6q.15.15.213.325t.062.375t-.062.375t-.213.325l-4.6 4.6q-.275.275-.7.275t-.7-.275t-.275-.7t.275-.7z',
  ],
  'chevron-up': [
    'm12 10.775l-3.9 3.9q-.275.275-.7.275t-.7-.275t-.275-.7t.275-.7l4.6-4.6q.15-.15.325-.213T12 8.4t.375.063t.325.212l4.6 4.6q.275.275.275.7t-.275.7t-.7.275t-.7-.275z',
  ],
  copy: [
    'M9 18q-.825 0-1.412-.587T7 16V4q0-.825.588-1.412T9 2h9q.825 0 1.413.588T20 4v12q0 .825-.587 1.413T18 18zm-4 4q-.825 0-1.412-.587T3 20V7q0-.425.288-.712T4 6t.713.288T5 7v13h10q.425 0 .713.288T16 21t-.288.713T15 22z',
  ],
  error: [
    'M12.713 16.713Q13 16.425 13 16t-.288-.712T12 15t-.712.288T11 16t.288.713T12 17t.713-.288m0-4Q13 12.425 13 12V8q0-.425-.288-.712T12 7t-.712.288T11 8v4q0 .425.288.713T12 13t.713-.288M12 22q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22',
  ],
  'expand-more': [
    'M12 14.95q-.2 0-.375-.062t-.325-.213l-4.6-4.6q-.275-.275-.275-.7t.275-.7t.7-.275t.7.275l3.9 3.9l3.9-3.9q.275-.275.7-.275t.7.275t.275.7t-.275.7l-4.6 4.6q-.15.15-.325.213T12 14.95',
  ],
  filter: [
    'M11 18q-.425 0-.712-.288T10 17t.288-.712T11 16h2q.425 0 .713.288T14 17t-.288.713T13 18zm-4-5q-.425 0-.712-.288T6 12t.288-.712T7 11h10q.425 0 .713.288T18 12t-.288.713T17 13zM4 8q-.425 0-.712-.288T3 7t.288-.712T4 6h16q.425 0 .713.288T21 7t-.288.713T20 8z',
  ],
  history: [
    'M12 21q-3.15 0-5.575-1.912T3.275 14.2q-.1-.375.15-.687t.675-.363q.4-.05.725.15t.45.6q.6 2.25 2.475 3.675T12 19q2.925 0 4.963-2.037T19 12t-2.037-4.962T12 5q-1.725 0-3.225.8T6.25 8H8q.425 0 .713.288T9 9t-.288.713T8 10H4q-.425 0-.712-.288T3 9V5q0-.425.288-.712T4 4t.713.288T5 5v1.35q1.275-1.6 3.113-2.475T12 3q1.875 0 3.513.713t2.85 1.924t1.925 2.85T21 12t-.712 3.513t-1.925 2.85t-2.85 1.925T12 21m1-9.4l2.5 2.5q.275.275.275.7t-.275.7t-.7.275t-.7-.275l-2.8-2.8q-.15-.15-.225-.337T11 11.975V8q0-.425.288-.712T12 7t.713.288T13 8z',
  ],
  location: [
    'M11.3 21.2q-.35-.125-.625-.375Q9.05 19.325 7.8 17.9t-2.087-2.762t-1.275-2.575T4 10.2q0-3.75 2.413-5.975T12 2t5.588 2.225T20 10.2q0 1.125-.437 2.363t-1.275 2.575T16.2 17.9t-2.875 2.925q-.275.25-.625.375t-.7.125t-.7-.125m2.113-9.787Q14 10.825 14 10t-.587-1.412T12 8t-1.412.588T10 10t.588 1.413T12 12t1.413-.587',
  ],
  lock: [
    'M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm7.413-5.587Q14 15.825 14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17t1.413-.587M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z',
  ],
  logout: [
    'M5 21q-.825 0-1.412-.587T3 19V5q0-.825.588-1.412T5 3h6q.425 0 .713.288T12 4t-.288.713T11 5H5v14h6q.425 0 .713.288T12 20t-.288.713T11 21zm12.175-8H10q-.425 0-.712-.288T9 12t.288-.712T10 11h7.175L15.3 9.125q-.275-.275-.275-.675t.275-.7t.7-.313t.725.288L20.3 11.3q.3.3.3.7t-.3.7l-3.575 3.575q-.3.3-.712.288t-.713-.313q-.275-.3-.262-.712t.287-.688z',
  ],
  menu: [
    'M4 18q-.425 0-.712-.288T3 17t.288-.712T4 16h16q.425 0 .713.288T21 17t-.288.713T20 18zm0-5q-.425 0-.712-.288T3 12t.288-.712T4 11h16q.425 0 .713.288T21 12t-.288.713T20 13zm0-5q-.425 0-.712-.288T3 7t.288-.712T4 6h16q.425 0 .713.288T21 7t-.288.713T20 8z',
  ],
  minus: ['M6 13q-.425 0-.712-.288T5 12t.288-.712T6 11h12q.425 0 .713.288T19 12t-.288.713T18 13z'],
  phone: [
    'M19.95 21q-3.125 0-6.175-1.362t-5.55-3.863t-3.862-5.55T3 4.05q0-.45.3-.75t.75-.3H8.1q.35 0 .625.238t.325.562l.65 3.5q.05.4-.025.675T9.4 8.45L6.975 10.9q.5.925 1.187 1.787t1.513 1.663q.775.775 1.625 1.438T13.1 17l2.35-2.35q.225-.225.588-.337t.712-.063l3.45.7q.35.1.575.363T21 15.9v4.05q0 .45-.3.75t-.75.3',
  ],
  sort: [
    'M4 18q-.425 0-.712-.288T3 17t.288-.712T4 16h4q.425 0 .713.288T9 17t-.288.713T8 18zm0-5q-.425 0-.712-.288T3 12t.288-.712T4 11h10q.425 0 .713.288T15 12t-.288.713T14 13zm0-5q-.425 0-.712-.288T3 7t.288-.712T4 6h16q.425 0 .713.288T21 7t-.288.713T20 8z',
  ],
  trash: [
    'M7 21q-.825 0-1.412-.587T5 19V6q-.425 0-.712-.288T4 5t.288-.712T5 4h4q0-.425.288-.712T10 3h4q.425 0 .713.288T15 4h4q.425 0 .713.288T20 5t-.288.713T19 6v13q0 .825-.587 1.413T17 21zm3.713-4.288Q11 16.426 11 16V9q0-.425-.288-.712T10 8t-.712.288T9 9v7q0 .425.288.713T10 17t.713-.288m4 0Q15 16.426 15 16V9q0-.425-.288-.712T14 8t-.712.288T13 9v7q0 .425.288.713T14 17t.713-.288',
  ],
  undo: [
    'M8 19q-.425 0-.712-.288T7 18t.288-.712T8 17h6.1q1.575 0 2.738-1T18 13.5T16.838 11T14.1 10H7.8l1.9 1.9q.275.275.275.7t-.275.7t-.7.275t-.7-.275L4.7 9.7q-.15-.15-.213-.325T4.426 9t.063-.375T4.7 8.3l3.6-3.6q.275-.275.7-.275t.7.275t.275.7t-.275.7L7.8 8h6.3q2.425 0 4.163 1.575T20 13.5t-1.737 3.925T14.1 19z',
  ],
  upload: [
    'M6 20q-.825 0-1.412-.587T4 18v-2q0-.425.288-.712T5 15t.713.288T6 16v2h12v-2q0-.425.288-.712T19 15t.713.288T20 16v2q0 .825-.587 1.413T18 20zm5-12.15L9.125 9.725q-.3.3-.712.288T7.7 9.7q-.275-.3-.288-.7t.288-.7l3.6-3.6q.15-.15.325-.212T12 4.425t.375.063t.325.212l3.6 3.6q.3.3.288.7t-.288.7q-.3.3-.712.313t-.713-.288L13 7.85V15q0 .425-.288.713T12 16t-.712-.288T11 15z',
  ],
  visibility: [
    'M15.188 14.688Q16.5 13.375 16.5 11.5t-1.312-3.187T12 7T8.813 8.313T7.5 11.5t1.313 3.188T12 16t3.188-1.312m-5.1-1.276Q9.3 12.625 9.3 11.5t.788-1.912T12 8.8t1.913.788t.787 1.912t-.787 1.913T12 14.2t-1.912-.787m-4.2 3.787q-2.763-1.8-4.363-4.75q-.125-.225-.187-.462t-.063-.488t.063-.488t.187-.462q1.6-2.95 4.363-4.75T12 4t6.113 1.8t4.362 4.75q.125.225.188.463t.062.487t-.062.488t-.188.462q-1.6 2.95-4.362 4.75T12 19t-6.113-1.8',
  ],
  'visibility-off': [
    'm19.1 21.9l-3.5-3.45q-.875.275-1.775.413T12 19q-3.35 0-6.125-1.8t-4.35-4.75q-.125-.225-.187-.462t-.063-.488t.063-.488t.187-.462q.55-.975 1.175-1.9T4.15 7L2.075 4.9Q1.8 4.625 1.8 4.213t.3-.713q.275-.275.7-.275t.7.275l17 17q.275.275.288.688t-.288.712q-.275.275-.7.275t-.7-.275M12 16q.275 0 .525-.025t.5-.1l-5.4-5.4q-.075.25-.1.5T7.5 11.5q0 1.875 1.313 3.188T12 16m0-12q3.35 0 6.138 1.813t4.362 4.762q.125.2.188.438t.062.487t-.05.488t-.175.437q-.475.925-1.062 1.75t-1.313 1.55q-.35.35-.825.325t-.825-.375l-2-2q-.175-.175-.225-.413t.025-.487q.1-.325.15-.625t.05-.65q0-1.875-1.312-3.187T12 7q-.35 0-.65.05t-.625.15q-.25.075-.5.025T9.8 7l-.825-.825q-.475-.475-.312-1.1t.787-.8q.625-.125 1.263-.2T12 4m1.975 5.65q.275.325.462.713t.238.812q.025.2-.15.275t-.325-.075l-2.05-2.05Q12 9.175 12.088 9t.287-.175q.475.05.875.263t.725.562',
  ],
} as const satisfies Readonly<Record<string, readonly string[]>>;

export type IconName = keyof typeof ICON_PATHS;

export const ICON_NAMES = Object.keys(ICON_PATHS) as readonly IconName[];

/** Material Symbols Rounded icons (filled path data). */
export const ICON_FILLED = new Set<IconName>([
  'arrow-back',
  'calendar-today',
  'check-circle',
  'chevron-down',
  'chevron-right',
  'chevron-up',
  'copy',
  'error',
  'expand-more',
  'filter',
  'history',
  'location',
  'lock',
  'logout',
  'menu',
  'minus',
  'phone',
  'sort',
  'trash',
  'undo',
  'upload',
  'visibility',
  'visibility-off',
]);

/** Directional glyphs mirrored in RTL (components.md §6.3). */
export const ICON_MIRROR: Readonly<Partial<Record<IconName, true>>> = {
  arrow: true,
  'arrow-back': true,
  'arrow-left': true,
  'arrow-outward': true,
  'chevron-left': true,
  'chevron-right': true,
  logout: true,
  return: true,
  undo: true,
};

/**
 * The buyer prototype's own sprites where they differ from the panels'
 * (`frontend/apps/buyer-mobile/prototype/index.html` `icon-*`): buyer-web and buyer-mobile use them.
 */
export const BUYER_ICON_PATHS: Readonly<Partial<Record<IconName, readonly string[]>>> = {
  shield: ['M12 3 4 6v6c0 5 3.4 8.4 8 10 4.6-1.6 8-5 8-10V6l-8-3Z', 'm8.5 12 2.2 2.2 4.8-5'],
  sun: [
    circle(12, 12, 4),
    'M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41',
  ],
};

/** `panel`: the admin and merchant prototypes; `buyer`: the buyer prototype (both buyer channels). */
export type IconSet = 'panel' | 'buyer';

/** The path data of an icon in a set; the buyer set falls back to the shared sprite. */
export function iconPaths(name: IconName, set: IconSet = 'panel'): readonly string[] {
  return (set === 'buyer' ? BUYER_ICON_PATHS[name] : undefined) ?? ICON_PATHS[name];
}

export function iconUsesFill(name: IconName): boolean {
  return ICON_FILLED.has(name);
}

export function iconShouldMirror(name: IconName): boolean {
  return ICON_MIRROR[name] === true;
}
